import type { Operation } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';

import {
  captureGraphqlOperationError,
  shouldTitleOperation,
} from '@/utils/captureGraphqlError';
import { handleError } from '@/utils/errorUtils';

const CHANGE_REQUEST_MESSAGE =
  'You need to create a change request to perform this action.';

/**
 * Apollo `onError` handler for the GraphQL bridge, extracted from
 * `ApolloGraphqlProvider` so the branch selection is unit-testable without
 * React (RSP-6971 review M1). Three routes per captured error:
 *
 * - change-request message → `onChangeRequestRequired(operation)`, no capture
 * - allowlisted op (`shouldTitleOperation`) → `captureGraphqlOperationError`
 * - anything else → legacy `handleError` with byte-identical args
 *
 * The `graphql_operation` / `http_status` tags stay generic (every op); only
 * the titled/fingerprinted capture path is allowlist-gated.
 */
export const createGraphqlErrorHandler =
  (onChangeRequestRequired: (operation: Operation) => void) =>
  ({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
    const operationName = operation.operationName || 'anonymous';
    const operationTags = { graphql_operation: operationName };
    const titledCapture = shouldTitleOperation(operationName);

    if (graphQLErrors) {
      // Note: types are not correct, as graphqlErrors appears to be a simple object in some (maybe all cases) rather then an Exception like object
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      graphQLErrors.forEach((error: any) => {
        if (error.message?.includes(CHANGE_REQUEST_MESSAGE)) {
          onChangeRequestRequired(operation);

          return false;
        }

        if (titledCapture) {
          // RSP-6971: titled exception + per-operation fingerprint so
          // allowlisted ops render `GraphQLOperationError: <op> — <msg>`
          // instead of `<unknown>`.
          captureGraphqlOperationError(error, {
            operationName,
            kind: 'graphql',
            extra: { ...error },
          });

          return;
        }

        // Not allowlisted: original capture, unchanged.
        const captureContext = {
          extra: { operationName },
          tags: operationTags,
        };
        if (error instanceof GraphQLError) {
          handleError(error, captureContext, operationName);
        } else if ('message' in error) {
          // Avoids the "Object captured as exception with keys: extensions, message" message in Sentry, and get a unique error message instead
          handleError(
            error.message,
            {
              extra: { operationName, ...error },
              tags: operationTags,
            },
            operationName
          );
        } else {
          handleError(error, captureContext, operationName);
        }
      });
    }

    if (networkError) {
      // Retry once silently before reporting. Apollo's onError fires again
      // on the retry, so we guard against an infinite loop with a context
      // flag. Only capture if the retry also fails — this filters out the
      // ambient "TypeError: Failed to fetch" noise from tab-close /
      // navigation / transient network that resolves on retry, while still
      // reporting real backend / CORS / DNS failures that persist.
      const { retried } = operation.getContext();
      if (!retried) {
        operation.setContext({ retried: true });

        return forward(operation);
      }

      // RSP-6931: a backend 500 surfaces here as an Apollo networkError with
      // a `statusCode`. Tag it (`http_status`) so Sentry alerts can isolate
      // 5xx — e.g. a Slack alert on `graphql_operation:*Oversight*
      // http_status:500`. This is the reliable capture point for backend
      // 500s: the rest-api errorHandler swallows HttpError 5xx into Datadog
      // logs without a Sentry event, but the browser always sees the 500.
      const httpStatus =
        'statusCode' in networkError ? networkError.statusCode : undefined;

      if (titledCapture) {
        // RSP-6971: titled + fingerprinted, mirroring the graphQLErrors branch.
        captureGraphqlOperationError(networkError, {
          operationName,
          kind: 'network',
          tags: httpStatus ? { http_status: String(httpStatus) } : undefined,
        });
      } else {
        // Not allowlisted: original capture, unchanged.
        handleError(
          networkError,
          {
            extra: { operationName },
            tags: httpStatus
              ? { ...operationTags, http_status: String(httpStatus) }
              : operationTags,
          },
          operationName
        );
      }
    }
  };
